import React, { useEffect, useRef } from 'react';
import anish2d from '../../assets/anish-2d.webp';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './About.scss';

gsap.registerPlugin(ScrollTrigger);

const About = () => {
    const aboutRef = useRef(null);
    const titleRef = useRef(null);
    const contentRef = useRef(null);
    const imageRef = useRef(null);
    const statsRef = useRef([]);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo(titleRef.current,
                {
                    y: 50,
                    opacity: 0
                },
                {
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: 'top 80%',
                    },
                    y: 0,
                    opacity: 1,
                    duration: 1,
                    ease: 'power3.out'
                }
            );

            gsap.fromTo(contentRef.current.children,
                {
                    x: -50,
                    opacity: 0
                },
                {
                    scrollTrigger: {
                        trigger: contentRef.current,
                        start: 'top 70%',
                    },
                    x: 0,
                    opacity: 1,
                    duration: 0.8,
                    stagger: 0.15,
                    ease: 'power3.out'
                }
            );

            gsap.fromTo(imageRef.current,
                {
                    x: 50,
                    opacity: 0,
                    rotationY: 45
                },
                {
                    scrollTrigger: {
                        trigger: imageRef.current,
                        start: 'top 70%',
                    },
                    x: 0,
                    opacity: 1,
                    rotationY: 0,
                    duration: 1.2,
                    ease: 'power3.out'
                }
            );

            const stats = document.querySelectorAll('.stat-number');
            stats.forEach((stat) => {
                const target = parseInt(stat.getAttribute('data-target'), 10);
                gsap.fromTo(stat,
                    {
                        textContent: 0
                    },
                    {
                        scrollTrigger: {
                            trigger: stat,
                            start: 'top 80%',
                        },
                        textContent: target,
                        duration: 2,
                        snap: { textContent: 1 },
                        ease: 'power2.out',
                        onUpdate: function() {
                            stat.textContent = Math.ceil(stat.textContent);
                        }
                    }
                );
            });

        }, aboutRef);

        return () => ctx.revert();
    }, []);

    return (
        <section className="about" ref={aboutRef}>
            <div className="about-container">
                <h2 className="section-title" ref={titleRef}>
                    About Me
                </h2>

                <div className="about-content">
                    <div className="content-left" ref={contentRef}>
                        <div className="intro">
                            <h3 className="name-title">Hi, I'm Anish Lawrence 👋</h3>
                            <p className="subtitle">Senior Frontend Developer & Open Source Builder</p>
                        </div>

                        <div className="description">
                            <p>
                                I'm a <strong>Senior Frontend Developer</strong> with <strong>8+ years of experience</strong> building
                                web and mobile applications. I hold a <strong>Master's in Computer Science</strong> and work
                                professionally with <strong>React</strong> and <strong>TypeScript</strong> at companies like{' '}
                                <strong>Workato</strong> and <strong>DeepConverse</strong>.
                            </p>
                            <p>
                                My focus today is the <strong>React Native ecosystem</strong>. I'm building <strong>AniUI</strong> — an
                                MIT-licensed component library with <strong>149 production-ready components</strong>, a CLI, and a
                                shadcn-compatible registry — alongside <strong>AniUI Pro</strong> (premium templates),{' '}
                                <strong>AniUI Academy</strong> (interactive learning), and my brand site{' '}
                                <a href="https://anishl.dev" target="_blank" rel="noopener noreferrer">anishl.dev</a>.
                            </p>
                            <p>
                                I care about design systems developers actually want to use: copy-paste ownership, strict TypeScript,
                                accessibility via rn-primitives, and tooling that meets teams where they are (Expo, NativeWind, Uniwind).
                            </p>
                        </div>

                        <div className="highlights">
                            <div className="highlight-item">
                                <div className="highlight-icon">📦</div>
                                <div className="highlight-text">
                                    <h4>AniUI Ecosystem</h4>
                                    <p>149 open-source components plus Pro templates, Academy, and docs at aniui.dev</p>
                                </div>
                            </div>
                            <div className="highlight-item">
                                <div className="highlight-icon">🏢</div>
                                <div className="highlight-text">
                                    <h4>Enterprise Frontend</h4>
                                    <p>AI interfaces, NX monorepos, Chrome extensions, and design systems at scale</p>
                                </div>
                            </div>
                            <div className="highlight-item">
                                <div className="highlight-icon">🎓</div>
                                <div className="highlight-text">
                                    <h4>Teaching & Community</h4>
                                    <p>AniUI Academy — learn by doing with live code playgrounds and structured paths</p>
                                </div>
                            </div>
                        </div>

                        <div className="stats-grid">
                            <div className="stat-item" ref={el => statsRef.current[0] = el}>
                                <div className="stat-number" data-target="8">0</div>
                                <div className="stat-label">Years Experience</div>
                            </div>
                            <div className="stat-item" ref={el => statsRef.current[1] = el}>
                                <div className="stat-number" data-target="149">0</div>
                                <div className="stat-label">AniUI Components</div>
                            </div>
                            <div className="stat-item" ref={el => statsRef.current[2] = el}>
                                <div className="stat-number" data-target="4">0</div>
                                <div className="stat-label">Products Shipped</div>
                            </div>
                        </div>

                        <div className="cta-buttons">
                            <a 
                                href="https://aniui.dev"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-primary"
                            >
                                Explore AniUI
                            </a>
                            <a 
                                href="#experience" 
                                className="btn-secondary"
                                onClick={(e) => {
                                    e.preventDefault();
                                    document.getElementById('experience')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }}
                            >
                                View Experience
                            </a>
                        </div>
                    </div>

                    <div className="content-right" ref={imageRef}>
                        <div className="image-wrapper-3d">
                            <div className="image-card">
                                <img src={anish2d} alt="Anish Lawrence" className="profile-image" />
                                <div className="image-glow"></div>
                            </div>
                            <div className="floating-badge badge-1">
                                <span>8+ Years</span>
                            </div>
                            <div className="floating-badge badge-2">
                                <span>Open Source</span>
                            </div>
                            <div className="floating-badge badge-3">
                                <span>AniUI</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default About;
